import { useCallback, useEffect, useRef } from 'react';
import { WSMessage, WSMessageType, createWSMessage, parseWSMessage, SocialEvent } from '../types/websocket';
import { useMessageStore } from '../stores/messageStore';
import { USER_WS_URL } from '../common/MetaSetting';

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAYS = [2000, 3000, 5000, 8000, 10000, 15000, 20000, 25000, 30000, 30000];
const HEARTBEAT_INTERVAL = 30000;

let userWs: WebSocket | null = null;
let heartbeatTimer: NodeJS.Timeout | null = null;
let reconnectTimer: NodeJS.Timeout | null = null;
let reconnectAttempt = 0;
let pongReceived = true;
let missedHeartbeats = 0;
const MAX_MISSED_HEARTBEATS = 3;

type SocialEventCallback = (messageType: WSMessageType, event: SocialEvent) => void;
const socialEventCallbacks = new Map<string, SocialEventCallback>();

function startHeartbeat() {
  stopHeartbeat();
  pongReceived = true;
  missedHeartbeats = 0;
  heartbeatTimer = setInterval(() => {
    if (userWs && userWs.readyState === WebSocket.OPEN) {
      if (missedHeartbeats >= MAX_MISSED_HEARTBEATS) {
        console.warn('[UserWS] 连续心跳无响应，主动断开重连');
        userWs.close(4000, 'Heartbeat timeout');
        return;
      }
      const msg = createWSMessage(WSMessageType.HEARTBEAT, { timestamp: Date.now() });
      userWs.send(JSON.stringify(msg));
      missedHeartbeats++;
      pongReceived = false;
    }
  }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  missedHeartbeats = 0;
  pongReceived = true;
}

function scheduleReconnect() {
  if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
    console.warn('[UserWS] 重连次数已达上限');
    return;
  }
  if (reconnectTimer) return;

  const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt, RECONNECT_DELAYS.length - 1)];
  console.log(`[UserWS] 将在 ${delay}ms 后尝试第 ${reconnectAttempt + 1} 次重连`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectAttempt++;
    doConnect();
  }, delay);
}

function cancelReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempt = 0;
}

function handleIncomingMessage(event: MessageEvent) {
  try {
    const parsed = parseWSMessage(event.data);
    if (!parsed) return;

    if (parsed.messageType === WSMessageType.HEARTBEAT) {
      pongReceived = true;
      missedHeartbeats = 0;
      return;
    }

    if (parsed.messageType === WSMessageType.FRIEND_REQUEST
        || parsed.messageType === WSMessageType.FRIEND_STATUS_CHANGE
        || parsed.messageType === WSMessageType.GROUP_JOIN_REQUEST
        || parsed.messageType === WSMessageType.GROUP_JOIN_REQUEST_RESPONSE) {
      const socialEvent = parsed.messageContent as SocialEvent;
      console.log('[UserWS] 收到社交事件:', parsed.messageType, socialEvent.eventType, 'targetUserId=', socialEvent.targetUserId);
      useMessageStore.getState().addSocialEvent(socialEvent);
      socialEventCallbacks.forEach(cb => cb(parsed.messageType, socialEvent));
    }
  } catch (e) {
    console.error('[UserWS] 解析消息失败:', e);
  }
}

function doConnect() {
  if (userWs && (userWs.readyState === WebSocket.OPEN || userWs.readyState === WebSocket.CONNECTING)) {
    return;
  }

  try {
    userWs = new WebSocket(USER_WS_URL);

    userWs.onopen = () => {
      console.log('[UserWS] 已连接到 user-server WebSocket');
      reconnectAttempt = 0;
      startHeartbeat();
    };

    userWs.onclose = (event) => {
      console.log('[UserWS] 连接关闭:', event.code, event.reason);
      stopHeartbeat();
      if (!event.wasClean) {
        scheduleReconnect();
      }
    };

    userWs.onerror = () => {
      console.error('[UserWS] 连接错误');
      stopHeartbeat();
    };

    userWs.onmessage = handleIncomingMessage;
  } catch (e) {
    console.error('[UserWS] 创建连接失败:', e);
    scheduleReconnect();
  }
}

function userWsConnect() {
  if (userWs && (userWs.readyState === WebSocket.OPEN || userWs.readyState === WebSocket.CONNECTING)) {
    return;
  }
  cancelReconnect();
  doConnect();
}

function userWsDisconnect() {
  cancelReconnect();
  stopHeartbeat();

  if (userWs) {
    userWs.onclose = null;
    userWs.onerror = null;
    userWs.onmessage = null;
    userWs.onopen = null;
    if (userWs.readyState === WebSocket.OPEN || userWs.readyState === WebSocket.CONNECTING) {
      userWs.close(1000, 'Client disconnect');
    }
    userWs = null;
  }
}

function addSocialEventCallback(cb: SocialEventCallback): () => void {
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  socialEventCallbacks.set(id, cb);
  return () => { socialEventCallbacks.delete(id); };
}

export function useUserWebSocket() {
  const cleanupRef = useRef<(() => void) | null>(null);

  const connect = useCallback(() => {
    userWsConnect();
  }, []);

  const disconnect = useCallback(() => {
    userWsDisconnect();
  }, []);

  const onSocialEvent = useCallback((cb: SocialEventCallback) => {
    return addSocialEventCallback(cb);
  }, []);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  return {
    connect,
    disconnect,
    onSocialEvent,
    isConnected: () => userWs?.readyState === WebSocket.OPEN,
  };
}
